import pxToRem from "hooks/usePxToRem";

import {Box, Grid} from "@mui/system";

import {CustomAutoComplete, CustomAutocomplete} from "ui";
import {useState} from "react";
import {AutoCompleteData} from "ui/CustomAutoComplete/CustomAutoComplete";
import useHandlePaginatedOptions from "views/ProcessDetails/hooks/useHandlePaginatedOptions";
import {ConfirmModal, ProcessDatePicker} from "components";
import {Controller, useFormContext} from "react-hook-form";
import useChangeDAteAndAutocomplete from "views/ProcessDetails/hooks/useChangeDAteAndAutocomplete";
import {
  GetEmployees,
  GetPersonsByTypes,
  PersonTypes,
} from "services/api_v2/Persons/Persons.service";
import useHandleFieldLists from "views/ProcessDetails/hooks/useHandleFieldLists";
import {AgencyingSituation, ReferredBy, ServiceType} from "util/globalEnums";
import {
  GetAllTypes,
  GetCurrenciesList,
  GetLogisticAgency,
  GetTransportCompaniesList,
} from "services/api_v2/Processes/Processes.service";
import {getTooltipText} from "views/ProcessDetails/utils/getTooltipText";
import useOriginalOrAlternativeValue from "hooks/useOriginalOrAlternativeValue";

import {CorrespondenceModal} from "components/HelperIconAndModal";
import {FormControlLabel, Icon, IconButton, Switch} from "@mui/material";
import ProcessSelect from "views/ProcessDetails/refactor/components/ProcessSelect";
import ProcessTextField from "views/ProcessDetails/refactor/components/ProcessTextField";
import {
  getCorrespondenceModalContent,
  getCorrespondenceModalItems,
} from "views/ProcessDetails/refactor/utils/correspondence";

const Transactioners = () => {
  const [shipperOptions, setShipperOptions] = useState<AutoCompleteData[]>(
    [] as AutoCompleteData[],
  );

  const [consigneeOptions, setConsigneeOptions] = useState<AutoCompleteData[]>(
    [] as AutoCompleteData[],
  );

  const [notifyOptions, setNotifyOptions] = useState<AutoCompleteData[]>(
    [] as AutoCompleteData[],
  );

  const [brokerOptions, setBrokerOptions] = useState<AutoCompleteData[]>(
    [] as AutoCompleteData[],
  );
  const [representativeOptions, setRepresentativeOptions] = useState<
    AutoCompleteData[]
  >([] as AutoCompleteData[]);

  const [representativeFinalOptions, setRepresentativeFinalOptions] = useState<
    AutoCompleteData[]
  >([] as AutoCompleteData[]);

  const [sellerOptions, setSellerOptions] = useState<AutoCompleteData[]>(
    [] as AutoCompleteData[],
  );
  const [originAgentOptions, setOriginAgentOptions] = useState<
    AutoCompleteData[]
  >([] as AutoCompleteData[]);

  const [transportCompanyOptions, setTransportCompanyOptions] = useState<
    AutoCompleteData[]
  >([] as AutoCompleteData[]);

  const [currenciesOptions, setCurrenciesOptions] = useState<
    AutoCompleteData[]
  >([] as AutoCompleteData[]);

  const [logisticAgencyOptions, setLogisticAgencyOptions] = useState<
    AutoCompleteData[]
  >([] as AutoCompleteData[]);

  const [typeProcessOptions, setTypeProcessOptions] = useState<
    AutoCompleteData[]
  >([] as AutoCompleteData[]);

  const [indicationOptions, setIndicationOptions] = useState<
    AutoCompleteData[]
  >([] as AutoCompleteData[]);

  const {control} = useFormContext();
  const {valueOrEmptyString} = useOriginalOrAlternativeValue();

  const {handleChangeAutocomplete, mountOptions} =
    useChangeDAteAndAutocomplete();

  const {
    getOptions: getShipperOptions,
    onBottomScroll: onBottomScrollShipper,
    isLoadingOptions: isLoadingShipperOptions,
  } = useHandlePaginatedOptions();

  const {
    getOptions: getConsigneeOptions,
    onBottomScroll: onBottomScrollConsignee,
    isLoadingOptions: isLoadingConsigneeOptions,
  } = useHandlePaginatedOptions();
  const {
    getOptions: getNotifyOptions,
    onBottomScroll: onBottomScrollNotify,
    isLoadingOptions: isLoadingNotigyOptions,
  } = useHandlePaginatedOptions();

  const [insuranceAlertModalIsOpen, setInsuranceAlertModalIsOpen] =
    useState(false);

  const handleToggleInsuranceAlertModal = () => {
    setInsuranceAlertModalIsOpen(!insuranceAlertModalIsOpen);
  };

  const [searchFields, setSearchFields] = useState({
    shipper: "",
    consignee: "",
    notify: "",
    broker: "",
    representative: "",
    finalRepresentative: "",
    originAgent: "",
    cargoAgent: "",
    seller: "",
    carrierOrigin: "",
    carrierDestination: "",
    currencies: "",
    logisticAgency: "",
    typeProcess: "",
    indicationOptions: "",
  });

  const {
    getOptions: getBrokerOptions,
    onBottomScroll: onBottomScrollBroker,
    isLoadingOptions: isLoadingBrokerOptions,
  } = useHandleFieldLists(GetPersonsByTypes, null, [PersonTypes.customBroker]);

  const {
    getOptions: getRepresentativeOptions,
    onBottomScroll: onBottomScrollRepresentative,
    isLoadingOptions: isLoadingRepresentativeOptions,
  } = useHandleFieldLists(GetPersonsByTypes, null, [
    PersonTypes.representative,
  ]);

  const {
    getOptions: getRepresentativeFinalOptions,
    onBottomScroll: onBottomScrollRepresentativeFinal,
    isLoadingOptions: isLoadingRepresentativeFinalOptions,
  } = useHandleFieldLists(GetPersonsByTypes, null, [
    PersonTypes.representative,
  ]);

  const {
    getOptions: getAgentOptions,
    onBottomScroll: onBottomScrollAgent,
    isLoadingOptions: isLoadingAgentOptions,
  } = useHandleFieldLists(GetPersonsByTypes, null, [PersonTypes.cargoAgent]);

  const formatOp = (data: any) => {
    return data?.data?.flatMap((item: any) => ({
      label: `${item?.person?.name} - ${item?.person?.id}`,
      id: item?.person?.id,
    }));
  };

  const formatLogisticAgency = (data: any) => {
    return data?.data?.map((item: any) => ({
      label: `${item?.person?.name} - ${item?.person?.id}`,
      id: item?.person?.id,
    }));
  };

  const formatTypeProcess = (data: any) => {
    return data?.data?.map((item: any) => ({
      label: `${item?.name} - ${item?.id}`,
      id: item?.id,
    }));
  };

  const {
    getOptions: getLogisticAgencyOptions,
    onBottomScroll: onBottomScrollLogisticAgency,
    isLoadingOptions: isLoadingLogisticAgencyOptions,
  } = useHandleFieldLists(GetLogisticAgency, formatLogisticAgency);

  const {
    getOptions: getSellerOptions,
    onBottomScroll: onBottomScrollSeller,
    isLoadingOptions: isLoadingSellerOptions,
  } = useHandleFieldLists(
    (page: number, PAGE_LIMIT: number, search: string) =>
      GetEmployees({
        page,
        limit: PAGE_LIMIT,
        name: search,
        filter: {name: search},
      }),
    formatOp,
  );

  const {
    getOptions: getIndicationOptions,
    onBottomScroll: onBottomScrollIndication,
    isLoadingOptions: isLoadingIndicationOptions,
  } = useHandleFieldLists(GetEmployees, formatOp);

  const {
    getOptions: getCarrierOriginOptions,
    onBottomScroll: onBottomScrollCarrierOrigin,
    isLoadingOptions: isLoadingCarrierOriginOptions,
  } = useHandleFieldLists(GetTransportCompaniesList, formatOp);

  const {
    getOptions: getCarrierDestinationOptions,
    onBottomScroll: onBottomScrollCarrierDestination,
    isLoadingOptions: isLoadingCarrierDestinationOptions,
  } = useHandleFieldLists(GetTransportCompaniesList, formatOp);

  const {
    getOptions: getCurrenciesOptions,
    onBottomScroll: onBottomScrollCurrencies,
    isLoadingOptions: isLoadingCurrenciesOptions,
    pageState: currenciesPageState,
  } = useHandleFieldLists(GetCurrenciesList);

  const {
    getOptions: getTypeProcessOptions,
    onBottomScroll: onBottomScrollTypeProcess,
    isLoadingOptions: isLoadingTypeProcessOptions,
  } = useHandleFieldLists(GetAllTypes, formatTypeProcess);

  const [correspondenceModalIsOpen, setCorrespondenceModalIsOpen] =
    useState(false);
  const [correspondenceModalContent, setCorrespondenceModalContent] =
    useState("");
  const [correspondenceModalItems, setCorrespondenceModalItems] = useState<
    string[]
  >([]);
  const [correspondenceModalTitle, setCorrespondenceModalTitle] = useState("");

  const handleOpenHelperModal = (
    content: string,
    title: string,
    items?: string[],
  ) => {
    setCorrespondenceModalTitle(title);
    setCorrespondenceModalContent(content);
    setCorrespondenceModalItems(items ?? [content]);
    setCorrespondenceModalIsOpen(true);
  };

  const handleCloseModal = () => {
    setCorrespondenceModalIsOpen(false);
    setCorrespondenceModalContent("");
    setCorrespondenceModalItems([]);
  };

  const {getValues} = useFormContext();

  const isAiProcess =
    `${getValues("operation")?.[0]}${getValues("modality")?.[0]}` === "IA";

  return (
    <>
      <CorrespondenceModal
        correspondenceText={correspondenceModalContent}
        correspondenceItems={correspondenceModalItems}
        isOpen={correspondenceModalIsOpen}
        onClose={handleCloseModal}
        correspondenceTitle={correspondenceModalTitle}
      />
      <Grid container columnSpacing={pxToRem(8)} rowSpacing={pxToRem(20)}>
        <Controller
          name="shipper"
          control={control}
          render={({field: {value}}) => (
            <Grid size={3} display="flex" alignItems="start">
              <Box
                sx={{
                  borderLeft: `0.5px solid lightgrey`,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    handleOpenHelperModal(
                      getCorrespondenceModalContent(value),
                      "Exportador",
                      getCorrespondenceModalItems(value),
                    )
                  }
                >
                  <Icon>markunread_box</Icon>
                </IconButton>
              </Box>
              <CustomAutocomplete
                name="shipper"
                fullWidth
                textFieldProps={{
                  label: "Exportador",
                }}
                placeholder="Exportador"
                tooltipText={getTooltipText(value?.name)}
                loading={isLoadingShipperOptions}
                options={mountOptions(value, shipperOptions)}
                value={valueOrEmptyString(value?.name)}
                onOpen={() =>
                  getShipperOptions({isShipper: true}, setShipperOptions)
                }
                optionSecondaryContent
                onInputChange={(e, value) => {
                  if (e !== null) {
                    setSearchFields((prev) => ({...prev, shipper: value}));
                    getShipperOptions(
                      {isShipper: true, clientName: value},
                      setShipperOptions,
                    );
                  }
                }}
                getNextPage={() => {
                  onBottomScrollShipper(
                    {isShipper: true, clientName: searchFields.shipper},
                    setShipperOptions,
                  );
                }}
                onChange={(e, value) => {
                  handleChangeAutocomplete(e, value, "shipper");
                }}
                isOptionEqualToValue={(option, value) =>
                  value === option?.label
                }
              />
            </Grid>
          )}
        />
        <Controller
          name="consignee"
          control={control}
          render={({field: {value}}) => (
            <Grid size={3} display="flex" alignItems="start">
              <Box
                sx={{
                  borderLeft: `0.5px solid lightgrey`,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    handleOpenHelperModal(
                      getCorrespondenceModalContent(value),
                      "Importador",
                      getCorrespondenceModalItems(value),
                    )
                  }
                >
                  <Icon>markunread_box</Icon>
                </IconButton>
              </Box>
              <CustomAutocomplete
                name="consignee"
                placeholder="Importador"
                fullWidth
                textFieldProps={{
                  label: "Importador",
                }}
                tooltipText={getTooltipText(value?.name)}
                loading={isLoadingConsigneeOptions}
                options={mountOptions(value, consigneeOptions)}
                value={valueOrEmptyString(value?.name)}
                optionSecondaryContent
                onOpen={() =>
                  getConsigneeOptions({isConsignee: true}, setConsigneeOptions)
                }
                onInputChange={(e, value) => {
                  if (e !== null) {
                    setSearchFields((prev) => ({...prev, consignee: value}));
                    getConsigneeOptions(
                      {isConsignee: true, clientName: value},
                      setConsigneeOptions,
                    );
                  }
                }}
                getNextPage={() => {
                  onBottomScrollConsignee(
                    {isConsignee: true, clientName: searchFields.consignee},
                    setConsigneeOptions,
                  );
                }}
                onChange={(e, value) => {
                  handleChangeAutocomplete(e, value, "consignee");
                }}
                isOptionEqualToValue={(option, value) =>
                  value === option?.label
                }
              />
            </Grid>
          )}
        />
        <Controller
          name="notify"
          control={control}
          render={({field: {value}}) => (
            <Grid size={3} display="flex" alignItems="start">
              <Box
                sx={{
                  borderLeft: `0.5px solid lightgrey`,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    handleOpenHelperModal(
                      getCorrespondenceModalContent(value),
                      "Notify",
                      getCorrespondenceModalItems(value),
                    )
                  }
                >
                  <Icon>markunread_box</Icon>
                </IconButton>
              </Box>
              <CustomAutocomplete
                name="notify"
                placeholder="Notify"
                fullWidth
                textFieldProps={{
                  label: "Notify",
                }}
                tooltipText={getTooltipText(value?.name)}
                loading={isLoadingNotigyOptions}
                options={mountOptions(value, notifyOptions)}
                value={valueOrEmptyString(value?.name)}
                optionSecondaryContent
                onOpen={() =>
                  getNotifyOptions({isNotify: true}, setNotifyOptions)
                }
                onInputChange={(e, value) => {
                  if (e !== null) {
                    setSearchFields((prev) => ({...prev, notify: value}));
                    getNotifyOptions(
                      {isNotify: true, clientName: value},
                      setNotifyOptions,
                    );
                  }
                }}
                getNextPage={() => {
                  onBottomScrollNotify(
                    {isNotify: true, clientName: searchFields.notify},
                    setNotifyOptions,
                  );
                }}
                onChange={(e, value) => {
                  handleChangeAutocomplete(e, value, "notify");
                }}
                isOptionEqualToValue={(option, value) =>
                  value === option?.label
                }
              />
            </Grid>
          )}
        />
        <Grid size={3}>
          <Controller
            name="customsBroker"
            control={control}
            render={({field: {value}}) => (
              <CustomAutocomplete
                name="customsBroker"
                placeholder="Despachante"
                fullWidth
                textFieldProps={{
                  label: "Despachante",
                }}
                tooltipText={getTooltipText(value?.name)}
                loading={isLoadingBrokerOptions}
                options={mountOptions(value, brokerOptions)}
                value={valueOrEmptyString(value?.name)}
                optionSecondaryContent
                onOpen={() => getBrokerOptions(setBrokerOptions)}
                onInputChange={(e, value) => {
                  if (e !== null) {
                    setSearchFields((prev) => ({...prev, broker: value}));
                    getBrokerOptions(setBrokerOptions, 1, value);
                  }
                }}
                getNextPage={() => {
                  onBottomScrollBroker(setBrokerOptions, searchFields.broker);
                }}
                onChange={(e, value) => {
                  handleChangeAutocomplete(e, value, "customsBroker");
                }}
                isOptionEqualToValue={(option, value) =>
                  value === option?.label
                }
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="representative"
            control={control}
            render={({field: {value}}) => (
              <CustomAutocomplete
                name="representative"
                // placeholder="Representante"
                fullWidth
                textFieldProps={{
                  label: "Representante",
                }}
                tooltipText={getTooltipText(value?.name)}
                loading={isLoadingRepresentativeOptions}
                options={mountOptions(value, representativeOptions)}
                value={valueOrEmptyString(value?.name)}
                optionSecondaryContent
                onOpen={() =>
                  getRepresentativeOptions(setRepresentativeOptions)
                }
                onInputChange={(e, value) => {
                  if (e !== null) {
                    setSearchFields((prev) => ({
                      ...prev,
                      representative: value,
                    }));
                    getRepresentativeOptions(setRepresentativeOptions);
                  }
                }}
                getNextPage={() => {
                  onBottomScrollRepresentative(
                    setRepresentativeOptions,
                    searchFields.representative,
                  );
                }}
                onChange={(e, value) =>
                  handleChangeAutocomplete(e, value, "representative")
                }
                isOptionEqualToValue={(option, value) =>
                  value === option?.label
                }
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="representativeFinalDestination"
            control={control}
            render={({field: {value}}) => (
              <CustomAutocomplete
                name="representativeFinalDestination"
                placeholder="Repres. Destino Final"
                tooltipText={getTooltipText(value?.name)}
                fullWidth
                textFieldProps={{
                  label: "Representante destino final",
                }}
                loading={isLoadingRepresentativeFinalOptions}
                options={mountOptions(value, representativeFinalOptions)}
                value={valueOrEmptyString(value?.name)}
                optionSecondaryContent
                onOpen={() =>
                  getRepresentativeFinalOptions(setRepresentativeFinalOptions)
                }
                onInputChange={(e, value) => {
                  if (e !== null) {
                    setSearchFields((prev) => ({
                      ...prev,
                      finalRepresentative: value,
                    }));
                    getRepresentativeFinalOptions(
                      setRepresentativeFinalOptions,
                      1,
                      value,
                    );
                  }
                }}
                getNextPage={() => {
                  onBottomScrollRepresentativeFinal(
                    setRepresentativeFinalOptions,
                    searchFields.finalRepresentative,
                  );
                }}
                onChange={(e, value) =>
                  handleChangeAutocomplete(
                    e,
                    value,
                    "representativeFinalDestination",
                  )
                }
                isOptionEqualToValue={(option, value) =>
                  value === option?.label
                }
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="sale"
            control={control}
            render={({field: {value}}) => (
              <CustomAutocomplete
                name="sale"
                placeholder="Vendedor"
                fullWidth
                textFieldProps={{
                  label: "Vendedor",
                }}
                tooltipText={getTooltipText(value?.name)}
                loading={isLoadingSellerOptions}
                options={mountOptions(value, sellerOptions)}
                value={valueOrEmptyString(value?.name)}
                onOpen={() => getSellerOptions(setSellerOptions, 1, "")}
                onInputChange={(e, value) => {
                  if (e !== null) {
                    setSearchFields((prev) => ({...prev, seller: value}));
                    getSellerOptions(setSellerOptions, 1, value);
                  }
                }}
                getNextPage={() => {
                  onBottomScrollSeller(setSellerOptions, searchFields.seller);
                }}
                onChange={(e, value) =>
                  handleChangeAutocomplete(e, value, "sale")
                }
                isOptionEqualToValue={(option, value) =>
                  value === option?.label
                }
              />
            )}
          />
        </Grid>
        <Controller
          name="originAgent"
          control={control}
          render={({field: {value}}) => (
            <Grid size={3} display="flex" alignItems="start">
              <Box
                sx={{
                  borderLeft: `0.5px solid lightgrey`,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    handleOpenHelperModal(
                      getCorrespondenceModalContent(value),
                      "Agente de origem",
                      getCorrespondenceModalItems(value),
                    )
                  }
                >
                  <Icon>markunread_box</Icon>
                </IconButton>
              </Box>
              <CustomAutocomplete
                name="originAgent"
                placeholder="Agente de origem"
                fullWidth
                textFieldProps={{
                  label: "Agente de origem",
                }}
                tooltipText={getTooltipText(value?.name)}
                loading={isLoadingAgentOptions}
                options={mountOptions(value, originAgentOptions)}
                value={valueOrEmptyString(value?.name)}
                optionSecondaryContent
                onOpen={() => getAgentOptions(setOriginAgentOptions)}
                onInputChange={(e, value) => {
                  if (e !== null) {
                    setSearchFields((prev) => ({
                      ...prev,
                      originAgent: value,
                    }));
                    getAgentOptions(setOriginAgentOptions, 1, value);
                  }
                }}
                getNextPage={() => {
                  onBottomScrollAgent(
                    setOriginAgentOptions,
                    searchFields.originAgent,
                  );
                }}
                onChange={(e, value) => {
                  handleChangeAutocomplete(e, value, "originAgent");
                }}
                isOptionEqualToValue={(option, value) =>
                  value === option?.label
                }
              />
            </Grid>
          )}
        />
        {/* campos novos */}
        {!isAiProcess && (
          <Grid size={3}>
            <Controller
              name="logisticAgency"
              control={control}
              render={({field: {value}}) => (
                <CustomAutocomplete
                  name="logisticAgency"
                  placeholder="Ag. Logística"
                  fullWidth
                  textFieldProps={{
                    label: "Ag. Logística",
                  }}
                  tooltipText={getTooltipText(value?.name)}
                  loading={isLoadingLogisticAgencyOptions}
                  options={mountOptions(value, logisticAgencyOptions)}
                  value={valueOrEmptyString(value?.name)}
                  optionSecondaryContent
                  onOpen={() =>
                    getLogisticAgencyOptions(setLogisticAgencyOptions, 1, "")
                  }
                  onInputChange={(e, value) => {
                    if (e !== null) {
                      setSearchFields((prev) => ({
                        ...prev,
                        logisticAgency: value,
                      }));
                      getLogisticAgencyOptions(
                        setLogisticAgencyOptions,
                        1,
                        value,
                      );
                    }
                  }}
                  getNextPage={() => {
                    onBottomScrollLogisticAgency(
                      setLogisticAgencyOptions,
                      searchFields.logisticAgency,
                    );
                  }}
                  onChange={(e, value) => {
                    handleChangeAutocomplete(e, value, "logisticAgency");
                  }}
                  isOptionEqualToValue={(option, value) =>
                    value === option?.label
                  }
                />
              )}
            />
          </Grid>
        )}
        {!isAiProcess && (
          <Grid size={3}>
            <Controller
              name="carrierOrigin"
              control={control}
              render={({field: {value}}) => (
                <CustomAutocomplete
                  name="carrierOrigin"
                  placeholder="Transp. Origem"
                  fullWidth
                  textFieldProps={{
                    label: "Transp. Origem",
                  }}
                  tooltipText={getTooltipText(value?.name)}
                  loading={isLoadingCarrierOriginOptions}
                  options={mountOptions(value, transportCompanyOptions)}
                  value={valueOrEmptyString(value?.name)}
                  onOpen={() =>
                    getCarrierOriginOptions(setTransportCompanyOptions)
                  }
                  optionSecondaryContent
                  onInputChange={(e, value) => {
                    if (e !== null) {
                      setSearchFields((prev) => ({
                        ...prev,
                        carrierOrigin: value,
                      }));
                      getCarrierOriginOptions(
                        setTransportCompanyOptions,
                        1,
                        value,
                      );
                    }
                  }}
                  getNextPage={() => {
                    onBottomScrollCarrierOrigin(
                      setTransportCompanyOptions,
                      searchFields.carrierOrigin,
                    );
                  }}
                  onChange={(e, value) => {
                    handleChangeAutocomplete(e, value, "carrierOrigin");
                  }}
                  isOptionEqualToValue={(option, value) =>
                    value === option?.label
                  }
                />
              )}
            />
          </Grid>
        )}
        <Grid size={3}>
          <Controller
            name="carrierDestination"
            control={control}
            render={({field: {value}}) => (
              <CustomAutocomplete
                name="carrierDestination"
                placeholder="Transp. Destino"
                fullWidth
                textFieldProps={{
                  label: "Transp. Destino",
                }}
                tooltipText={getTooltipText(value?.name)}
                loading={isLoadingCarrierDestinationOptions}
                options={mountOptions(value, transportCompanyOptions)}
                value={valueOrEmptyString(value?.name)}
                optionSecondaryContent
                onOpen={() =>
                  getCarrierDestinationOptions(setTransportCompanyOptions)
                }
                onInputChange={(e, value) => {
                  if (e !== null) {
                    setSearchFields((prev) => ({
                      ...prev,
                      carrierDestination: value,
                    }));
                    getCarrierDestinationOptions(
                      setTransportCompanyOptions,
                      1,
                      value,
                    );
                  }
                }}
                getNextPage={() => {
                  onBottomScrollCarrierDestination(
                    setTransportCompanyOptions,
                    searchFields.carrierDestination,
                  );
                }}
                onChange={(e, value) =>
                  handleChangeAutocomplete(e, value, "carrierDestination")
                }
                isOptionEqualToValue={(option, value) =>
                  value === option?.label
                }
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="indication"
            control={control}
            render={({field: {value}}) => (
              <CustomAutocomplete
                name="indication"
                placeholder="Indicação"
                fullWidth
                textFieldProps={{
                  label: "Indicação",
                }}
                tooltipText={getTooltipText(value?.name)}
                loading={isLoadingIndicationOptions}
                options={mountOptions(value, indicationOptions)}
                value={valueOrEmptyString(value?.name)}
                onOpen={() => getIndicationOptions(setIndicationOptions, 1, "")}
                onInputChange={(e, value) => {
                  if (e !== null) {
                    setSearchFields((prev) => ({...prev, indication: value}));
                    getIndicationOptions(setIndicationOptions, 1, value);
                  }
                }}
                getNextPage={() => {
                  onBottomScrollIndication(
                    setIndicationOptions,
                    searchFields.indicationOptions,
                  );
                }}
                onChange={(e, value) =>
                  handleChangeAutocomplete(e, value, "indication")
                }
                isOptionEqualToValue={(option, value) =>
                  value === option?.label
                }
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="referredBy"
            control={control}
            render={({field: {value, onChange}}) => (
              <ProcessSelect
                name="referredBy"
                data={Object.values(ReferredBy) as string[]}
                value={value}
                onChange={onChange}
                required={false}
                label="Encaminhado por"
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="processType"
            control={control}
            render={({field: {value}}) => (
              <CustomAutocomplete
                name={`processType`}
                fullWidth
                textFieldProps={{
                  label: "Tipo de processo",
                }}
                placeholder="Tipo de processo"
                tooltipText={getTooltipText(value?.name)}
                loading={isLoadingTypeProcessOptions}
                options={mountOptions(value, typeProcessOptions)}
                value={valueOrEmptyString(value?.name)}
                onOpen={() => getTypeProcessOptions(setTypeProcessOptions)}
                onInputChange={(e, value) => {
                  if (e !== null) {
                    setSearchFields((prev) => ({
                      ...prev,
                      typeProcess: value,
                    }));
                    getTypeProcessOptions(setTypeProcessOptions, 1, value);
                  }
                }}
                getNextPage={() => {
                  onBottomScrollTypeProcess(
                    setTypeProcessOptions,
                    searchFields.typeProcess,
                  );
                }}
                onChange={(e, value) => {
                  handleChangeAutocomplete(e, value, "processType");
                }}
                isOptionEqualToValue={(option, value) =>
                  value === option?.label
                }
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="serviceType"
            control={control}
            render={({field: {value, onChange}}) => (
              <ProcessSelect
                name="serviceType"
                data={Object.values(ServiceType)}
                value={value}
                onChange={onChange}
                required={false}
                label="Tipo de serviço"
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="agencyingSituation"
            control={control}
            render={({field: {value, onChange}}) => (
              <ProcessSelect
                name="agencyingSituation"
                value={value}
                onChange={onChange}
                data={Object.values(AgencyingSituation)}
                disabled
                label="Situação do agenciamento"
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="currency"
            control={control}
            render={({field: {value}}) => (
              <CustomAutoComplete
                size="small"
                label="Moeda do processo"
                name="currency"
                placeholder="Moeda do processo"
                isDisabled
                isLoading={isLoadingCurrenciesOptions}
                tooltipText={getTooltipText(value?.name)}
                value={valueOrEmptyString(value?.name)}
                options={currenciesOptions}
                onChangeFn={(e, value) =>
                  handleChangeAutocomplete(e, value, "currency")
                }
                onOpen={() => getCurrenciesOptions(setCurrenciesOptions)}
                onBottomScroll={() =>
                  onBottomScrollCurrencies(
                    setCurrenciesOptions,
                    searchFields.currencies,
                  )
                }
                hasMorePagesToLoad={
                  currenciesPageState.currentPage < currenciesPageState.lastPage
                }
                onInputChange={(e, value) => {
                  //condição pra não fazer a requisição na primeira atualização do input

                  if (e !== null) {
                    setSearchFields((prev) => ({
                      ...prev,
                      currencies: value,
                    }));
                    getCurrenciesOptions(setCurrenciesOptions, 1, value);
                  }
                }}
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="quotation"
            control={control}
            render={({field: {value}}) => (
              <CustomAutoComplete
                size="small"
                label="Cotação"
                isDisabled
                name="quotation"
                placeholder="Cotação"
                tooltipText={getTooltipText(value?.name)}
                value={valueOrEmptyString(value?.name)}
                options={[]}
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="dateValidityFees"
            control={control}
            render={({field: {value}}) => (
              <ProcessDatePicker
                value={value}
                name="dateValidityFees"
                title="Data de validade das taxas"
                label="Data de validade das taxas"
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="isPriority"
            control={control}
            render={({field: {value, onChange}}) => (
              <FormControlLabel
                label="Prioritário"
                // labelPlacement="start"
                control={
                  <Switch
                    checked={value}
                    onChange={onChange}
                    name="isPriority"
                  />
                }
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="dateCancellation"
            control={control}
            render={({field: {value}}) => (
              <ProcessDatePicker
                value={value}
                name="dateCancellation"
                title="Data do cancelamento"
                label="Data do cancelamento"
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="logisticCancellationProcess"
            control={control}
            render={({field: {value}}) => (
              <CustomAutoComplete
                size="small"
                label="Motivo de cancelamento"
                name="logisticCancellationProcess"
                placeholder="Motivo de cancelamento"
                value={valueOrEmptyString(value?.name)}
                options={[]}
                tooltipText={getTooltipText(value?.name)}
                isDisabled
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <Controller
            name="detailsCancellation"
            control={control}
            render={({field: {onChange, value}}) => (
              <ProcessTextField
                name="detailsCancellation"
                placeholder="Detalhes do cancelamento"
                value={value}
                onChange={onChange}
                size="small"
                label="Detalhes do cancelamento"
              />
            )}
          />
        </Grid>
        <ConfirmModal
          isOpen={insuranceAlertModalIsOpen}
          color="warning"
          title="Esse processo não possui seguro cadastrado."
          subtitle="Para conseguir selecionar uma seguradora é necessário primeiro ir até a seção de dados da carga e marcar positivamente a opção de seguro."
          confirmButtonText="Confirmar"
          confirmButtonIconIsDisabled
          titleIcon="warning"
          confirmButtonFn={handleToggleInsuranceAlertModal}
          onClose={handleToggleInsuranceAlertModal}
          hasOnlyConfirmButton
        />
      </Grid>
    </>
  );
};

export default Transactioners;
